import axios from 'axios'
import {
    normalizeKinoBdDetails,
    normalizeKinoBdSearch,
    normalizeKinoBdSummary,
} from '../../utils/kinobdMedia.js'

export const KINOBD_API = String(process.env.VUE_APP_KINOBD_API_BASE || 'https://kinobd.net/api')
    .replace(/\/$/, '')
export const KINOBD_PLAYERDATA = String(
    process.env.VUE_APP_KINOBD_PLAYERDATA_URL || 'https://kinobd.net/playerdata'
)
export const KINOBD_TIMEOUT = 10000
export const KINOBD_SEARCH_CACHE_TTL = 5 * 60 * 1000
export const KINOBD_DISCOVERY_CACHE_TTL = 15 * 60 * 1000
export const KINOBD_DETAILS_CACHE_TTL = 30 * 60 * 1000
export const KINOBD_PREFERRED_PROVIDER = String(
    process.env.VUE_APP_KINOBD_PREFERRED_PROVIDER || 'alloha'
).trim().toLowerCase()
export const KINOBD_LANGUAGE = String(process.env.VUE_APP_KINOBD_LANGUAGE || 'en')
    .trim().toLowerCase()

function responseData(result, fallback) {
    return result.status === 'fulfilled' ? result.value.data : fallback
}

function timestamp(value) {
    let parsed = value ? new Date(value).getTime() : NaN
    return Number.isFinite(parsed) ? parsed : null
}

function nullableNumber(value) {
    if (value === null || value === undefined || value === '') return null
    let parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

function boundedPositiveInteger(value, fallback, maximum = Number.MAX_SAFE_INTEGER) {
    let parsed = Number(value)
    if (!Number.isFinite(parsed)) return fallback
    return Math.max(1, Math.min(maximum, Math.floor(parsed)))
}

function kinoBdStatus(item) {
    if (!item || item.type !== 'serial') return 'released'
    let yearEnd = String(item.year_end || '').trim().toLowerCase()
    if (['...', '…', '-', '—', 'ongoing'].includes(yearEnd)) return 'ongoing'
    return yearEnd ? 'released' : 'unknown'
}

function normalizeKinoBdCandidate(item, index) {
    let media = normalizeKinoBdSummary(item)
    if (!media) return null
    let status = kinoBdStatus(item)
    return {
        source: 'kinobd-top',
        media: {
            ...media,
            description: item.description || '',
            durationMinutes: nullableNumber(item.time_minutes),
            premiere: item.premiere_ru || item.premiere_world || '',
            releaseStatus: status,
            backdropCandidates: [media.poster].filter(Boolean),
        },
        signals: {
            ratings: {
                kinopoisk: nullableNumber(item.rating_kp),
                imdb: nullableNumber(item.rating_imdb),
                shikimori: null,
            },
            votes: {
                kinopoisk: nullableNumber(item.rating_kp_count),
                imdb: nullableNumber(item.rating_imdb_count),
                shikimori: null,
            },
            popularity: nullableNumber(item.popular_rate),
            premiereAt: item.premiere_world || item.premiere_ru || null,
            updatedAt: timestamp(item.updated_at),
            status,
            playbackQuality: null,
            sourceRank: index,
        },
    }
}

const PLAYBACK_ORDER = [
    'collaps', 'vibix', 'kodik', 'videoseed', 'hdvb', 'ashdi', 'alloha',
    'flixcdn', 'turbo', 'videocdn', 'bazon', 'kinobd', 'trailer',
]

export function normalizeKinoBdPlaybackSources(data, currentOrigin = '', preferredProvider = '') {
    let order = new Map(PLAYBACK_ORDER.map((provider, index) => [provider, index]))
    let preferred = String(preferredProvider || '').trim().toLowerCase()
    let seen = new Set()
    return Object.entries(data && typeof data === 'object' ? data : {})
        .map(([provider, value], index) => {
            if (!value || typeof value !== 'object' || !value.iframe) return null
            try {
                let url = new URL(String(value.iframe))
                if (url.protocol !== 'https:' || (currentOrigin && url.origin === currentOrigin)) return null
                if (seen.has(url.href)) return null
                seen.add(url.href)
                return {
                    provider: String(provider).toLowerCase(),
                    src: url.href,
                    quality: String(value.quality || '').trim(),
                    translation: String(value.translate || '').trim(),
                    index,
                }
            } catch (_) {
                return null
            }
        })
        .filter(Boolean)
        .filter((source) => source.provider !== 'torrent' && source.provider !== 'nf')
        .sort((first, second) => {
            if (preferred && first.provider !== second.provider) {
                if (first.provider === preferred) return -1
                if (second.provider === preferred) return 1
            }
            return (order.get(first.provider) ?? PLAYBACK_ORDER.length) - (order.get(second.provider) ?? PLAYBACK_ORDER.length)
                || first.index - second.index
        })
}

export function createKinoBdProvider(client = axios) {
    let searchCache = new Map()
    let discoveryCache = new Map()
    let detailsCache = new Map()

    function readCache(cache, key, ttl) {
        let cached = cache.get(key)
        if (!cached || Date.now() - cached.savedAt > ttl) {
            cache.delete(key)
            return null
        }
        return cached.value
    }

    function writeCache(cache, key, value) {
        cache.set(key, { savedAt: Date.now(), value })
        return value
    }

    let provider = {
        id: 'kinobd',
        capabilities: Object.freeze({
            anime: false,
            movies: true,
            series: true,
            search: true,
            details: true,
            playerById: true,
            iframe: true,
            widget: false,
            multipleSources: true,
            embeddedEpisodeSelector: true,
            embeddedVoiceSelector: true,
            structuredEpisodes: false,
            structuredTranslations: false,
        }),
        async discoverTop({ signal, page = 1, limit = 100 } = {}) {
            let pageNumber = boundedPositiveInteger(page, 1)
            let pageSize = boundedPositiveInteger(limit, 100, 100)
            let cacheKey = `${pageNumber}:${pageSize}`
            let cached = readCache(discoveryCache, cacheKey, KINOBD_DISCOVERY_CACHE_TTL)
            if (cached) return cached
            let response = await client.get(`${KINOBD_API}/films/top`, {
                params: { page: pageNumber, per_page: pageSize },
                signal,
                timeout: KINOBD_TIMEOUT,
            })
            let values = response.data && response.data.data
            let candidates = (Array.isArray(values) ? values : [])
                .map(normalizeKinoBdCandidate)
                .filter(Boolean)
            return candidates.length
                ? writeCache(discoveryCache, cacheKey, candidates)
                : []
        },
        async search(query, { signal } = {}) {
            let normalizedQuery = String(query || '').trim()
            if (normalizedQuery.length < 3) return []
            let cacheKey = normalizedQuery.toLocaleLowerCase('ru')
            let cached = readCache(searchCache, cacheKey, KINOBD_SEARCH_CACHE_TTL)
            if (cached) return cached
            let response = await client.get(`${KINOBD_API}/films/search/title`, {
                params: { q: normalizedQuery, page: 1 },
                signal,
                timeout: KINOBD_TIMEOUT,
            })
            return writeCache(
                searchCache,
                cacheKey,
                normalizeKinoBdSearch(response.data && response.data.data, normalizedQuery)
            )
        },
        async getDetails(id, { signal } = {}) {
            let internalId = Number(id)
            if (!Number.isInteger(internalId) || internalId <= 0) return null
            let cached = readCache(detailsCache, internalId, KINOBD_DETAILS_CACHE_TTL)
            if (cached) return cached
            let options = { signal, timeout: KINOBD_TIMEOUT }
            let requests = await Promise.allSettled([
                client.get(`${KINOBD_API}/films/${internalId}`, {
                    ...options,
                    params: { with: 'genres,countries' },
                }),
                client.get(`${KINOBD_API}/films/${internalId}/images`, options),
                client.get(`${KINOBD_API}/films/${internalId}/credits`, options),
            ])
            if (requests[0].status === 'rejected') throw requests[0].reason
            return writeCache(detailsCache, internalId, normalizeKinoBdDetails(
                requests[0].value.data,
                responseData(requests[1], {}),
                responseData(requests[2], {})
            ))
        },
        async resolve(reference, { signal } = {}) {
            if (reference.provider === 'kinobd' && reference.providerId) {
                try {
                    let hinted = await provider.getDetails(reference.providerId, { signal })
                    let hintedId = {
                        kinopoisk: hinted && hinted.ids && hinted.ids.kinopoisk,
                        imdb: hinted && hinted.ids && hinted.ids.imdb,
                        tmdb: hinted && hinted.ids && hinted.ids.tmdb,
                    }[reference.source]
                    if (
                        reference.source === 'provider'
                        || (hinted && String(hintedId || '') === String(reference.id))
                    ) return hinted
                } catch (error) {
                    if (signal && signal.aborted) throw error
                }
            }
            if (reference.source === 'provider' && reference.provider === 'kinobd') {
                return provider.getDetails(reference.id, { signal })
            }

            let searchType = { kinopoisk: 'kp_id', imdb: 'imdb_id' }[reference.source]
            if (!searchType) return null
            let response = await client.get(`${KINOBD_API}/films/search/${searchType}`, {
                params: { q: reference.id, page: 1 },
                signal,
                timeout: KINOBD_TIMEOUT,
            })
            let values = response.data && response.data.data
            let expectedType = reference.kind === 'series' ? 'serial' : 'film'
            let idField = reference.source === 'kinopoisk' ? 'kinopoisk_id' : 'imdb_id'
            let match = (Array.isArray(values) ? values : []).find((item) => {
                return item && item.type === expectedType && String(item[idField] || '') === String(reference.id)
            })
            return match ? provider.getDetails(match.id, { signal }) : null
        },
        getPlayer(media) {
            let playback = media && media.playbackRef || {}
            let internalId = Number(playback.internalId || media && media.id)
            if (!Number.isInteger(internalId) || internalId <= 0) return null
            return {
                provider: 'kinobd',
                mode: 'sources',
                internalId: String(internalId),
                kinopoiskId: playback.kinopoiskId || media && media.ids && media.ids.kinopoisk || null,
                title: playback.title || media && media.title || 'KinoBD',
                preferredProvider: KINOBD_PREFERRED_PROVIDER,
                language: KINOBD_LANGUAGE,
            }
        },
        async getPlaybackSources(descriptor, { signal, pageOrigin = '' } = {}) {
            let internalId = Number(descriptor && descriptor.internalId)
            if (!Number.isInteger(internalId) || internalId <= 0) return []
            let parameters = new URLSearchParams({ inid: String(internalId), fast: '1' })
            if (descriptor.kinopoiskId) parameters.set('kinopoisk', descriptor.kinopoiskId)
            let preferredProvider = String(
                descriptor.preferredProvider || KINOBD_PREFERRED_PROVIDER
            ).trim().toLowerCase()
            let language = String(descriptor.language || KINOBD_LANGUAGE).trim().toLowerCase()
            if (preferredProvider) parameters.set('player', preferredProvider)
            if (language) parameters.set('language', language)
            let response = await client.post(KINOBD_PLAYERDATA, parameters.toString(), {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Re': pageOrigin,
                },
                signal,
                timeout: KINOBD_TIMEOUT,
            })
            return normalizeKinoBdPlaybackSources(response.data, pageOrigin, preferredProvider)
        },
    }
    return provider
}

export const kinoBdProvider = createKinoBdProvider()

export { normalizeKinoBdCandidate }
